import { type FC, type FormEvent, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import './styles/styles.css';

export interface FilterFieldOption {
  value: string;
  label: string;
}

export interface FilterField {
  /** Becomes the key in the emitted search object. */
  key: string;
  /** Present => a dropdown of these values; absent => a free-text input. */
  options?: FilterFieldOption[];
  /** Dropdown only: adds a leading empty option — the "no filter" sentinel. */
  allLabel?: string;
  /** Text input only: placeholder text. */
  placeholder?: string;
  /** Initial value. */
  defaultValue?: string;
  ariaLabel?: string;
}

/** Selected values keyed by `FilterField.key`; empty selections are omitted. */
export type FilterValues = Record<string, string>;

export interface FilterBarProps {
  fields: FilterField[];
  /** Called on Search with the current non-empty selections. */
  onSearch: (values: FilterValues) => void;
  searchLabel?: string;
  /** Toolbar row by default; `stacked` (column) suits a future modal/popover body. */
  layout?: 'inline' | 'stacked';
  className?: string;
}

const buildInternalValues = (fields: FilterField[]): FilterValues =>
  Object.fromEntries(fields.map((field) => [field.key, field.defaultValue ?? '']));

const stripEmpty = (values: FilterValues): FilterValues =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));

/**
 * Seed a consumer's initial search state from the same field defaults the bar starts with,
 * so the first data load matches the bar's initial selection (single source of truth,
 * avoids a redundant fetch on mount).
 */
export const getInitialFilterValues = (fields: FilterField[]): FilterValues =>
  stripEmpty(buildInternalValues(fields));

/**
 * Domain-agnostic filter bar: N fields + a Search button. A field with `options` renders as
 * a dropdown; without `options` it renders as a free-text input. It knows nothing about the
 * fields it renders — the caller supplies field configs and, on Search (button click or
 * Enter), receives a plain object (`{ [field.key]: value }`, empties omitted). Values are
 * strings; the caller casts them to its own enums/types.
 *
 * Container-agnostic: `layout="stacked"` (+ the `className` passthrough) lets the same bar
 * drop into a modal/popover body later without any API change.
 */
export const FilterBar: FC<FilterBarProps> = ({
  fields,
  onSearch,
  searchLabel = 'Search',
  layout = 'inline',
  className,
}) => {
  const [values, setValues] = useState<FilterValues>(() => buildInternalValues(fields));

  const setField = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const classes = ['filter-bar', `filter-bar--${layout}`, className]
    .filter(Boolean)
    .join(' ');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSearch(stripEmpty(values));
  };

  return (
    <form className={classes} onSubmit={handleSubmit}>
      {fields.map((field) =>
        field.options ? (
          <Form.Select
            key={field.key}
            className="filter-bar__field"
            aria-label={field.ariaLabel ?? field.key}
            value={values[field.key] ?? ''}
            onChange={(event) => setField(field.key, event.target.value)}
          >
            {field.allLabel !== undefined && <option value="">{field.allLabel}</option>}
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            key={field.key}
            className="filter-bar__field"
            type="text"
            placeholder={field.placeholder}
            aria-label={field.ariaLabel ?? field.key}
            value={values[field.key] ?? ''}
            onChange={(event) => setField(field.key, event.target.value)}
          />
        ),
      )}
      <Button type="submit" className="filter-bar__search">
        {searchLabel}
      </Button>
    </form>
  );
};
